import { useEffect, useState } from 'react'

/**
 * Shared "current wall-clock" ticker.
 *
 * Previously each hook that needed a periodic "is this due yet?" re-render
 * (useReminders, upcoming useContactFreshness, etc.) ran its own
 * `setInterval(() => setNow(Date.now()), 60_000)`. At N such hooks mounted
 * simultaneously you wake the event loop N times per tick and every one
 * triggers a separate React render. Funnelling through this single hook
 * collapses that to one timer per unique `intervalMs`, shared via a
 * module-level subscription list.
 *
 * Usage:
 *   const now = useNow(60_000) // re-renders at most once per minute
 *   const isDue = reminder.remindAt <= now
 *
 * Guarantees:
 *   - All consumers of the same `intervalMs` tick on the same edge.
 *   - Tearing down the last subscriber clears the timer — no idle-wake cost
 *     when no hook is mounted.
 *   - `intervalMs` is frozen per-call: passing a different value after mount
 *     is ignored (React hooks semantics — pass a stable primitive).
 */
const subscribers = new Map<number, {
  timer: ReturnType<typeof setInterval> | null
  listeners: Set<(now: number) => void>
}>()

function subscribe(intervalMs: number, listener: (now: number) => void): () => void {
  let bucket = subscribers.get(intervalMs)
  if (!bucket) {
    bucket = { timer: null, listeners: new Set() }
    subscribers.set(intervalMs, bucket)
  }
  bucket.listeners.add(listener)
  if (bucket.timer == null) {
    bucket.timer = setInterval(() => {
      const now = Date.now()
      // Snapshot listeners before iterating — listeners may unsubscribe
      // synchronously in response to the tick.
      const snapshot = Array.from(bucket!.listeners)
      for (const fn of snapshot) fn(now)
    }, intervalMs)
  }
  return () => {
    const b = subscribers.get(intervalMs)
    if (!b) return
    b.listeners.delete(listener)
    if (b.listeners.size === 0 && b.timer != null) {
      clearInterval(b.timer)
      b.timer = null
      subscribers.delete(intervalMs)
    }
  }
}

export function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => subscribe(intervalMs, setNow), [intervalMs])
  return now
}
